import { MouseEvent } from 'react'
import { Alert, Table } from 'antd'
import { useQuery } from '@apollo/client'
import {
    DFM_NO_ACTION_REQUIRED,
    DFM_STATES,
    ImDfmStateType,
} from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/ImDfmSlide/constants'
import StatusOverviewIcon from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/ImDfmSlide/InactiveSlide/StatusOverviewIcon'

import Box from '~/core/Box'
import Link from '~/core/Link'
import Text from '~/core/Text'
import logger from '~/lib/logger'

import dfmTopicsTableQuery from './dfmTopicsTableQuery'
import styles from './styles.css'

type DfmTopicsTablePropsType = {
    onClickTopic: (dfmId: string) => void
    placement: string
    qmrId: string
    toggleDfmTopicsTable: (evt: Event) => void
    isHighTouch: boolean
    isImQuoteType: boolean
    isNonImDfmEnabled: boolean
}

type RecordType = {
    id: string
    topic: {
        label: string
    }
    title: string
    status: ImDfmStateType
    acceptedAt: string
}

const DfmTopicsTable = (props: DfmTopicsTablePropsType) => {
    const {
        onClickTopic,
        placement,
        qmrId,
        toggleDfmTopicsTable,
        isHighTouch,
        isImQuoteType,
        isNonImDfmEnabled,
    } = props

    const { data, error, loading } = useQuery(dfmTopicsTableQuery, {
        variables: { id: qmrId },
        skip: !qmrId,
        fetchPolicy: 'network-only',
    })

    if (error) {
        logger.exception(error, {
            info: 'DfmCategorySection/PartRow/DfmTopicsTable:: Failed to query dfmTopicsTableQuery',
            qmrId,
        })

        return (
            <Alert
                message="Unable to load DFM topics, please refresh and try again."
                showIcon
                type="error"
            />
        )
    }

    const dfmFeedback = data?.quoteModelRevision?.dfmFeedback
    const showTitle = !isImQuoteType && isHighTouch && isNonImDfmEnabled
    const getColumns = () => {
        return [
            {
                key: showTitle ? 'title' : 'topic',
                title: showTitle ? 'Title' : 'Topic',
                width: '50%',
                render: (record: RecordType) => {
                    const { id: dfmId, topic, title } = record

                    return (
                        (topic?.label || title) && (
                            <Link
                                data-test-target={`im-part-dfm-${dfmId}`}
                                name={`im-part-dfm-${dfmId}`}
                                onClick={(evt: Event) => {
                                    evt.preventDefault()
                                    onClickTopic(dfmId)
                                }}
                                placement={placement}
                            >
                                {!isImQuoteType &&
                                isNonImDfmEnabled &&
                                isHighTouch
                                    ? title || 'No title specified'
                                    : topic?.label}
                            </Link>
                        )
                    )
                },
            },
            {
                key: 'status',
                title: 'Status',
                width: '50%',
                render: (record: RecordType) => {
                    const { status } = record

                    const statusColor =
                        status === DFM_NO_ACTION_REQUIRED
                            ? 'blackAlpha45'
                            : 'blackAlpha65'

                    return (
                        <>
                            <StatusOverviewIcon status={status} />
                            <Text
                                color={statusColor}
                                data-test-target="im-dfm-status"
                                fontSize="14px"
                                fontWeight={300}
                            >
                                {DFM_STATES[status]}
                            </Text>
                        </>
                    )
                },
            },
        ]
    }

    return (
        <Table
            className={styles.dfmTopicTable}
            columns={getColumns()}
            dataSource={dfmFeedback}
            footer={() => (
                <Box justifyContent="center">
                    <Link
                        data-test-target={`collapse-part-row-dfms`}
                        name="collapse-part-row-dfms"
                        onClick={toggleDfmTopicsTable}
                        placement={placement}
                        style={{ fontSize: '14px' }}
                    >
                        {`Hide DFM ${
                            !isImQuoteType && isHighTouch && isNonImDfmEnabled
                                ? 'titles'
                                : 'topics'
                        }`}
                    </Link>
                </Box>
            )}
            loading={loading}
            onRow={(record: RecordType) => {
                return {
                    onClick: (evt: MouseEvent) => {
                        evt.preventDefault()
                        onClickTopic(record?.id)
                    },
                }
            }}
            pagination={false}
            rowClassName={() => 'expandedTableRow'}
            rowKey="id"
            style={{ width: '100%' }}
        />
    )
}

export default DfmTopicsTable
